<template>
  <q-btn-group
    spread
    :dense="$q.platform.is.mobile"
  >
    <template v-if="!isDate">
      <q-btn
        v-for="(weekday, index) in weekdays"
        :key="index"
        :dense="$q.platform.is.mobile"
        :color="getWeekdayProperty(selectedWeekdays, index, 'color')"
        :text-color="getWeekdayProperty(selectedWeekdays, index, 'textColor')"
        :label="weekday"
        no-caps
        @click="onSetActiveWeekday(index)"
      />
    </template>
    <template
      v-if="allowDate"
    >
      <q-separator vertical />
      <q-btn-dropdown
        ref="datePicker"
        icon="calendar_month"
        :label="dateLabel"
        :dropdown-icon="($q.platform.is.mobile && !isDate) ? 'none' : null"
        :dense="$q.platform.is.mobile"
        :text-color="dateButtonTextColor"
        :color="dateButtonColor"
        class="q-pl-sm"
      >
        <div class="row justify-center items-center q-col-gutter-sm">
          <q-date
            v-model="selectedDate"
            no-unset
            minimal
            flat
            :options="preventPast"
            clearable
            mask="YYYY/MM/DD"
          />
          <div
            v-if="isDate"
          >
            <q-btn
              :label="$t('common.buttonClear')"
              flat
              color="negative"
              class="col-12"
              @click="selectedDate = null"
            />
          </div>
        </div>
      </q-btn-dropdown>
    </template>
  </q-btn-group>
</template>
<script>
import { Timestamp } from 'firebase/firestore';
import {
  get, includes, map, xor,
} from 'lodash';
import { useTime } from 'src/composables/time';

export default {
  name: 'WeekdayPicker',

  props: {
    modelValue: {
      type: [Array, Object],
      required: false,
      default: () => ([]),
    },
    dense: {
      type: Boolean,
      default: false,
    },
    allowDate: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue'],

  setup() {
    const { getLocaleDateString, formatDate, extractDate } = useTime();
    return { getLocaleDateString, formatDate, extractDate };
  },

  data() {
    return {
      selectedWeekdays: this.modelValue,

      weekdays: map(this.$tm('common.weekdays'), (weekday) => weekday.substring(0, 2)),
    };
  },

  computed: {
    isDate() {
      return this.modelValue instanceof Timestamp;
    },

    dateLabel() {
      if (!this.isDate) {
        return null;
      }
      return this.getLocaleDateString(this.modelValue);
    }, // dateLabel()

    dateButtonColor() {
      return this.isDate ? 'primary' : 'grey-2';
    },

    dateButtonTextColor() {
      return this.isDate ? 'white' : 'primary';
    },

    selectedDate: {
      get() {
        if (!(this.modelValue instanceof Timestamp)) {
          return null;
        }
        return this.formatDate(this.modelValue.toDate(), 'yyyy/MM/dd');
      },
      set(d) {
        if (d === null) {
          this.$emit('update:modelValue', null);
        } else {
          this.$emit('update:modelValue', Timestamp.fromDate(
            this.extractDate(d, 'yyyy/MM/dd'),
          ));
        }
        this.$refs.datePicker.hide();
      },
    },

  },

  watch: {
    modelValue: {
      deep: true,
      immediate: false,
      handler(val) {
        this.selectedWeekdays = val;
      },
    },
  },

  methods: {

    onSetActiveWeekday(index) {
      this.selectedWeekdays = xor(this.selectedWeekdays, [index]);
      this.$emit('update:modelValue', this.selectedWeekdays);
    }, // onSetActiveWeekday()

    getWeekdayProperty(selectedWeekdays, weekday, property) {
      const activeWeekday = {
        color: 'primary',
        textColor: 'white',
      };
      const inactiveWeekday = {
        color: 'grey-2',
        textColor: 'primary',
      };

      if (includes(selectedWeekdays, weekday)) {
        return get(activeWeekday, property);
      }
      return get(inactiveWeekday, property);
    }, // getWeekdayProperty()

    preventPast(d) {
      return d >= this.formatDate(new Date(), 'yyyy/MM/dd');
    }, // preventPast()
  },
};
</script>
